import queryKeys from '@/constants/queryKeys';
import { getAdminContentDetail } from '@/services/api/requests/content';
import { TContentDetail } from '@/types/Content.types';
import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '@/store/useAppStore';

const PROCESSING_POLL_MS = 4000;

const isMuxProcessing = (content?: TContentDetail): boolean => {
  const details = (content?.details ?? {}) as { source?: string; mux?: { status?: string } };
  if (details.source !== 'mux') return false;
  const status = details.mux?.status;
  return !!status && status !== 'ready' && status !== 'errored';
};

const useAdminContentDetail = (id: string, options?: { pollWhileProcessing?: boolean }) => {
  const isAuthenticated = useAppStore((state) => !!state.user);

  const { isPending, isLoading, isSuccess, data, isRefetching, refetch } = useQuery({
    queryKey: [queryKeys.content.byId, id],
    // Polled queries must stop when there is no session. Otherwise the interval
    // can fire during sign-out teardown, 401, and walk the whole refresh path for
    // a user who is already on their way out.
    // This one polls every few seconds on a page an admin sits and watches, so it
    // is the likeliest of the four to fire mid-teardown.
    enabled: isAuthenticated,
    queryFn: async () => {
      const response = await getAdminContentDetail(id);
      return response;
    },
    // While a Mux video is still transcoding, poll so the UI flips to publishable
    // once the webhook marks it ready.
    refetchInterval: options?.pollWhileProcessing
      ? (query) => (isMuxProcessing(query.state.data as TContentDetail | undefined) ? PROCESSING_POLL_MS : false)
      : false,
  });

  return { isPending, isLoading, isSuccess, data, isRefetching, refetch };
};

export default useAdminContentDetail;
